<template>
    <div v-if="insideGroup" class="doc-radio-row">
        <div class="doc-radio-caption">
            <span v-if="item.cditem" class="doc-radio-code">
                {{ item.cditem }}
            </span>

            <span class="doc-radio-text">
                {{ item.nmcaption }}
                <span v-if="isObrigatorio" class="required">*</span>
            </span>
        </div>

        <div class="doc-radio-info">
            <button
                v-if="podeInformar || item.informacoes"
                type="button"
                class="doc-info-button"
                :class="{
                    'doc-info-button--required': precisaInformacao && !item.informacoes,
                    'doc-info-button--filled': item.informacoes
                }"
                title="Informações complementares"
                @click="abrirInformacoes"
            >
                📝
            </button>
        </div>

        <div class="doc-radio-options">
            <label
                v-for="opcao in opcoes"
                :key="opcao.value"
                class="doc-radio-cell"
                :class="{ 'doc-radio-cell--checked': item.resposta_opcoes === opcao.value }"
                :title="opcao.label"
            >
                <input
                    type="radio"
                    :name="`radio-${item.id}`"
                    :value="opcao.value"
                    :checked="item.resposta_opcoes === opcao.value"
                    @change="onChange(opcao.value)"
                />

                <span></span>
            </label>
        </div>

        <DocInfoModal ref="infoModalRef" />
    </div>

    <div v-else class="form-item">
        <label v-if="!insideTable" class="label-caption">
            {{ item.nmcaption }}
            <span v-if="isObrigatorio" class="required">*</span>
        </label>
        
        <div class="radio-group">
            <label
                v-for="opcao in opcoes"
                :key="opcao.value"
                class="radio-option"
            >
                <input
                    type="radio"
                    class="radio-input"
                    :name="`radio-${item.id}`"
                    :value="opcao.value"
                    :checked="item.resposta_opcoes === opcao.value"
                    @change="onChange(opcao.value)"
                />

                <span class="radio-label">{{ opcao.label }}</span>
            </label>

            <button
                v-if="podeInformar || item.informacoes"
                type="button"
                class="info-button-normal"
                @click="abrirInformacoes"
            >
                {{ item.informacoes ? 'Editar informações' : 'Adicionar informações' }}
            </button>
        </div>

        <!-- <button
            v-if="podeInformar || item.informacoes"
            type="button"
            class="info-button-normal"
            @click="abrirInformacoes"
        >
            {{ item.informacoes ? 'Editar informações' : 'Adicionar informações' }}
        </button> -->

        <DocInfoModal ref="infoModalRef" />
    </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import DocInfoModal from './DocInfoModal.vue';
import { itemPrecisaInformacao, parseListaValores, parseOpcoes } from './docUtils';

const props = defineProps({
    item: {
        type: Object,
        required: true
    },
    opcoesGrupo: {
        type: Array,
        default: () => []
    },
    opcoesCkObsGrupo: {
        type: String,
        default: null
    },
    insideGroup: {
        type: Boolean,
        default: false
    },
    insideTable: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['update:item']);

const infoModalRef = ref(null);

const isObrigatorio = computed(() => props.item.ckobrigatorio === 'S');

const opcoes = computed(() => {
    if (props.item.opcoes) return parseOpcoes(props.item.opcoes);

    return props.opcoesGrupo.map(opcao => ({
        value: opcao,
        label: opcao
    }));
});

const precisaInformacao = computed(() => {
    return itemPrecisaInformacao(props.item, props.opcoesCkObsGrupo);
});

const podeInformar = computed(() => {
    if (props.item.ckinformacoes === 'S') return true;
    if (precisaInformacao.value) return true;

    const listaObs = parseListaValores(props.item.opcoes_ckobs || props.opcoesCkObsGrupo);
    return listaObs.length > 0 && props.item.resposta_opcoes;
});

function onChange(value) {
    emit('update:item', {
        ...props.item,
        resposta_opcoes: value
    });
}

async function abrirInformacoes() {
    const texto = await infoModalRef.value.showModal(props.item);
    if (texto === null) return;

    emit('update:item', {
        ...props.item,
        informacoes: texto?.trim() || null
    });
}
</script>

<style scoped>
.doc-radio-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 42px auto;
    gap: 6px;
    align-items: center;

    padding: 7px 0;
    border-bottom: 1px solid #e5e7eb;
}

.doc-radio-row:hover {
    background: #f9fafb;
}

.doc-radio-caption {
    display: flex;
    align-items: flex-start;
    gap: 7px;
    min-width: 0;
    text-align: left;
}

.doc-radio-code {
    flex: 0 0 42px;
    font-weight: 700;
    color: #1f2937;
    font-size: 13px;
    line-height: 1.35;
}

.doc-radio-text {
    flex: 1;
    font-size: 13px;
    color: #374151;
    line-height: 1.35;
    text-align: left;
}

.doc-radio-info {
    width: 42px;
    display: flex;
    justify-content: center;
}

.doc-info-button {
    width: 32px;
    height: 28px;
    border: 1px solid transparent;
    border-radius: 5px;
    background: transparent;
    cursor: pointer;
    opacity: .65;
}

.doc-info-button:hover {
    opacity: 1;
    background: #eef5f4;
    border-color: #4a827c;
}

.doc-info-button--required {
    opacity: 1;
    background: #fff7ed;
    border-color: #b45309;
}

.doc-info-button--filled {
    opacity: 1;
    background: #dff1ef;
    border-color: #4a827c;
}

.doc-radio-options {
    display: grid;
    grid-template-columns: repeat(4, 42px);
    gap: 4px;
}

.doc-radio-cell {
    width: 42px;
    height: 30px;

    display: flex;
    align-items: center;
    justify-content: center;

    border: 1px solid #d1d5db;
    border-radius: 5px;
    background: #fff;
    cursor: pointer;
}

.doc-radio-cell input {
    display: none;
}

.doc-radio-cell span {
    width: 13px;
    height: 13px;
    border: 1px solid #9ca3af;
    border-radius: 50%;
    background: #fff;
}

.doc-radio-cell--checked {
    background: #dff1ef;
    border-color: #4a827c;
}

.doc-radio-cell--checked span {
    background: #4a827c;
    border-color: #4a827c;
    box-shadow: inset 0 0 0 3px #fff;
}

.info-button-normal {
    margin-top: 8px;
    border: 1px solid #4a827c;
    background: #eef8f7;
    color: #24534f;
    border-radius: 4px;
    padding: 5px 9px;
    font-size: 12px;
    cursor: pointer;
}

/* layout original */
.radio-group {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 14px;
    align-items: flex-start;
}

.radio-option {
    display: inline-flex;
    align-items: flex-start;
    gap: 6px;
    padding: 6px 10px;
    border: 1px solid #d0d0d0;
    border-radius: 4px;
    background: #f9f9f9;
    cursor: pointer;
    transition: .15s;
}

.radio-option:hover {
    background: #eef5f4;
    border-color: #4a827c;
}

.radio-option:has(input:checked) {
    background: #dff1ef;
    border-color: #4a827c;
    box-shadow: inset 0 0 0 1px #4a827c;
}

.radio-input {
    width: 15px !important;
    height: 15px !important;
    min-height: 15px !important;
    padding: 0;
    margin: 2px 0 0 0;
    flex: 0 0 15px;
    cursor: pointer;
}

.radio-label {
    display: inline-block;
    max-width: 100%;
    word-break: break-word;
}

.required {
    color: #b00020;
    margin-left: 3px;
}
</style>